import type { ReactElement, RefObject } from 'react'
import { FileJson, RefreshCw } from 'lucide-react'
import type { ImportEndpointItem } from '../../../../types/case-container'
import type { ImportResult } from './use-json-import-modal'

const ERROR_PREVIEW_LIMIT = 5
const PREVIEW_LIMIT = 10

interface SuccessViewProps {
  result: ImportResult
  onClose: () => void
}

export function SuccessView({ result, onClose }: SuccessViewProps): ReactElement {
  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-950/30">
        <p className="font-medium text-green-700 dark:text-green-400">
          Import completed: {result.imported} imported, {result.skipped} skipped
        </p>
        {result.errors.length > 0 && (
          <div className="mt-2 text-sm text-red-600 dark:text-red-400">
            {result.errors.length} error(s):
            <ul className="mt-1 list-inside list-disc">
              {result.errors.slice(0, ERROR_PREVIEW_LIMIT).map((err, index) => (
                <li key={index}>
                  Row {err.row}: {err.field} - {err.message}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      <div className="flex justify-end">
        <button
          onClick={onClose}
          className="rounded-lg bg-[#4e5e82] px-4 py-2 text-sm font-medium text-white hover:bg-[#5c6e94]"
        >
          Done
        </button>
      </div>
    </div>
  )
}

interface InputSectionProps {
  file: File | null
  jsonText: string
  fileInputRef: RefObject<HTMLInputElement>
  onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  onTextChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void
}

export function InputSection({
  file,
  jsonText,
  fileInputRef,
  onFileChange,
  onTextChange,
}: InputSectionProps): ReactElement {
  return (
    <div>
      <p className="mb-3 text-sm text-slate-600 dark:text-slate-400">
        Upload a JSON file or paste JSON. Expected format: array of objects or object with
        &quot;endpoints&quot; array. Each object should have{' '}
        <code className="rounded bg-slate-100 px-1 py-0.5 text-xs dark:bg-slate-800">hostname</code>{' '}
        (required).
      </p>
      <input
        ref={fileInputRef}
        type="file"
        accept=".json"
        onChange={onFileChange}
        className="hidden"
      />
      <button
        onClick={() => fileInputRef.current?.click()}
        className="mb-3 flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-slate-300 p-4 transition-colors hover:border-lime-400 dark:border-slate-600 dark:hover:border-[#4e5e82]"
      >
        <FileJson className="h-6 w-6 text-slate-400" />
        <span className="text-slate-600 dark:text-slate-400">
          {file ? file.name : 'Click to select JSON file'}
        </span>
      </button>
      <div className="mb-3 text-center text-sm text-slate-400 dark:text-slate-500">
        or paste JSON below
      </div>
      <textarea
        value={jsonText}
        onChange={onTextChange}
        placeholder='[{"hostname": "WKSTN-001", "type": "workstation", "ip": "192.168.1.10"}]'
        className="h-32 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 font-mono text-sm text-slate-900 placeholder:text-slate-400 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:placeholder:text-slate-500"
      />
    </div>
  )
}

export function ErrorBanner({ message }: { message: string }): ReactElement {
  return (
    <div className="rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-950/30">
      <p className="text-sm text-red-600 dark:text-red-400">{message}</p>
    </div>
  )
}

export function PreviewTable({ items }: { items: ImportEndpointItem[] }): ReactElement {
  return (
    <div>
      <h4 className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-300">
        Preview (first {PREVIEW_LIMIT} items)
      </h4>
      <div className="overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 dark:text-slate-400">
                Hostname
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 dark:text-slate-400">
                Type
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 dark:text-slate-400">
                IP
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
            {items.map((item, index) => (
              <tr key={index}>
                <td className="px-3 py-2 text-slate-900 dark:text-slate-100">{item.hostname}</td>
                <td className="px-3 py-2 text-slate-600 dark:text-slate-400">{item.deviceType}</td>
                <td className="px-3 py-2 text-slate-600 dark:text-slate-400">
                  {item.ipAddresses?.[0] ?? '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

interface ActionsProps {
  onClose: () => void
  onImport: () => void
  importing: boolean
  previewCount: number
}

export function Actions({
  onClose,
  onImport,
  importing,
  previewCount,
}: ActionsProps): ReactElement {
  return (
    <div className="flex justify-end gap-3 pt-4">
      <button
        type="button"
        onClick={onClose}
        className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800"
      >
        Cancel
      </button>
      <button
        onClick={onImport}
        disabled={importing || previewCount === 0}
        className="inline-flex items-center gap-2 rounded-lg bg-[#4e5e82] px-4 py-2 text-sm font-medium text-white hover:bg-[#5c6e94] disabled:opacity-50"
      >
        {importing && <RefreshCw className="h-4 w-4 animate-spin" />}
        Import {previewCount > 0 ? `(${previewCount.toString()}+ endpoints)` : ''}
      </button>
    </div>
  )
}
